import React, { useEffect, useState } from 'react';

import { CursorEditor, Select } from '@aline/common';
import { Typography } from '@mui/material';
import { ContractTypePicker, EditableDocName } from 'src/components';
import { AlineTooltip, Spacer } from 'src/components/generic';
import { DocSyncedIcon } from 'src/components/icons/DocSyncedIcon';
import { DocSyncingIcon } from 'src/components/icons/DocSyncingIcon';
import type { GridDocumentStatusInput } from 'src/components/reports/report-detail/grid-document-status';
import { DocumentStatusMenu } from 'src/components/reports/report-detail/GridStatusCell';
import { useEditorTabStateSelectorForTabId } from 'src/editor/editor-tab-state-registry';
import {
  CopyDocLinkButton,
  DownloadDocButton,
  EditCollaboratorsButton,
  VersionHistoryButton,
} from 'src/editor/toolbars';
import { Apollo } from 'src/gql/generated';
import { useGetCurrentDoc } from 'src/hooks';
import { useEditorLoading, useIsEditorVariant } from 'src/layout-engine';
import { useCurrentTabIdOrThrow } from 'src/layouts/tabs/tab-active-context';
import { makeStyles } from 'tss-react/mui';

import { PresenceAvatars } from './components/PresenceAvatars';
import { ZoomLevelDropdown } from './components/ZoomLevelDropdown';
import { DocumentAppBarButtons } from './DocumentAppBarButtons';
import { TemplateAppBarButtons } from './TemplateAppBarButtons';

const useStyles = makeStyles()(theme => ({
  appBar: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(1.5),
    padding: theme.spacing(0, 2),
    width: '100%',
  },
  // Note: The width is needed so the Saved vs. Saving... doesn't cause the check in icon to jump.
  savingText: {
    fontColor: theme.appColors.secondaryType,
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    marginLeft: theme.spacing(1),
    width: 50,
  },
}));

function StandardEditorAppBarButtons(): JSX.Element | null {
  const { document } = useGetCurrentDoc();

  if (!document) return null;

  switch (document.variant) {
    case Apollo.DocumentVariant.Template: {
      return <TemplateAppBarButtons />;
    }
    default: {
      return <DocumentAppBarButtons document={document} />;
    }
  }
}

export function StandardEditorAppBarContent({ documentId }: { documentId: UUID }): JSX.Element {
  const { classes } = useStyles();
  const { document } = useGetCurrentDoc();
  const editorLoading = useEditorLoading();
  const isCursorEditor = useIsEditorVariant(CursorEditor.isCursorEditor);

  return (
    <div className={classes.appBar}>
      <EditableDocName disabled={editorLoading} />
      <SyncingDoc />
      <Spacer />
      {document?.latestVersion.__typename === 'StaticFileVersion' && <ZoomLevelDropdown />}
      {isCursorEditor && <PresenceAvatars />}
      {document && (
        <>
          <EditorDocumentStatus document={document} />
          <ContractTypePicker
            document={document}
            popoverProps={{
              anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
              transformOrigin: { horizontal: 'right', vertical: 'top' },
            }}
          />
        </>
      )}
      <EditCollaboratorsButton disabled={editorLoading} documentId={documentId} />
      <CopyDocLinkButton disabled={editorLoading} documentId={documentId} />
      <VersionHistoryButton disabled={editorLoading} documentId={documentId} />
      <DownloadDocButton disabled={editorLoading} documentId={documentId} />
      <StandardEditorAppBarButtons />
    </div>
  );
}

const useSyncingDocStyles = makeStyles()(theme => ({
  container: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(1.5),
  },
  syncText: {
    fontSize: 14,
  },
}));

function EditorDocumentStatus({
  document,
}: {
  document: NonNullable<ReturnType<typeof useGetCurrentDoc>['document']>;
}): JSX.Element | null {
  const statusInput = getEditorDocumentStatusInput(document);
  if (!statusInput) return null;

  return <DocumentStatusMenu documentId={document.id} statusInput={statusInput} />;
}

function getEditorDocumentStatusInput(
  document: NonNullable<ReturnType<typeof useGetCurrentDoc>['document']>
): GridDocumentStatusInput | null {
  const version = document.latestVersion;
  if (!version) return null;

  const workflow = document.workflow
    ? {
        envelopeStatus: document.workflow.envelopeStatus,
        isSignedOutsideAline: document.workflow.isSignedOutsideAline,
        wasSentForSig: document.workflow.wasSentForSig,
      }
    : null;

  return {
    isCertificate: document.isCertificate,
    isSigned: document.isSigned,
    latestVersion: version,
    requesterRole: document.requesterRole ?? null,
    status: document.status,
    variant: document.variant,
    workflow,
    workflowFileRole: document.workflowFileRole ?? null,
  };
}

function SyncingDoc(): JSX.Element {
  const { classes } = useSyncingDocStyles();
  const tabId = useCurrentTabIdOrThrow();
  const syncingDoc = useEditorTabStateSelectorForTabId(tabId, state => state.viewer.syncingDoc);
  const isInitialized = syncingDoc !== 'uninitialized';
  const [showSaved, setShowSaved] = useState(false);

  useEffect(() => {
    if (syncingDoc === false) {
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 3000);
    }
  }, [syncingDoc]);

  switch (syncingDoc) {
    case false:
    case 'uninitialized': {
      const showText = isInitialized && showSaved;

      return (
        <div className={classes.container} data-cy={Select.document.editor.appBar.syncStatus}>
          <AlineTooltip arrow placement="bottom" title={!showText ? 'Document is synced' : ''}>
            <DocSyncedIcon />
          </AlineTooltip>
          {showText && (
            <Typography noWrap className={classes.syncText}>
              Saved to Aline
            </Typography>
          )}
        </div>
      );
    }
    case true: {
      return (
        <div className={classes.container} data-cy={Select.document.editor.appBar.syncStatus}>
          <DocSyncingIcon />
          <Typography className={classes.syncText}>Saving...</Typography>
        </div>
      );
    }
  }
}
